import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  FormAlertService,
  MapAction,
  MapExtentUtils,
  MapId,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  PortalId,
  PzService,
  ShapeUtils,
  SourceType,
  StreetService,
  ToolType,
  auxiliaryLandParcelToolState,
  getFileLoaderFormFieldConfigs,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FileSaverService } from 'ngx-filesaver';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { MainRoutes } from '../../../guards/guard.models';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { SessionService } from '../../../services/session/session.service';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  GACSDRNewRequestControlName,
  GACSDRNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { GACSDRNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  GACSDRNewRequest,
  GACSDRNewRequestDto,
  GACSDRNewRequestFormWorkspaceState,
} from '../services/new-request/new-request.model';
import { WorkPurposeDictionaryService } from '../services/work-purpose-dictionary/work-purpose-dictionary.service';

@Component({
  selector: 'app-gacsdr-new-request',
  templateUrl: './new-request.component.html',
  styleUrls: ['./new-request.component.scss'],
  standalone: false,
})
export class GACSDRNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = GACSDRNewRequestControlName;
  override formGroup: FormGroup<GACSDRNewRequestFormGroup>;
  mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('typeName', 'GK.MAP.TYPE', true),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    false,
    true,
    '25',
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    [MapObjectApiType.ExtentOrPolygon],
    true,
  );
  mapSettings: MapSettings;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  successSigned = false;
  isArchivalWorkPurposeVisible = true;
  override portalId = PortalId.GeodeticAndCartographicServiceDataRequest;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.GeodeticAndCartographicServiceDataRequestNewRequestFormVersion;
  workspaceStateId =
    WorkspaceStateId.GeodeticAndCartographicServiceDataRequestNewRequestForm;
  downloadingPreviewFile = false;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private gacsdrNewRequestFormService: GACSDRNewRequestFormService,
    public override streetService: StreetService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
    public workPurposeDictionaryService: WorkPurposeDictionaryService,
    public sessionService: SessionService,
    public override fileSaverService: FileSaverService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
  }

  createForm(): void {
    this.formGroup = this.gacsdrNewRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.GeodeticAndCartographicServiceDataRequest,
      this.mapStateService.getViewState(
        MapId.GeodeticAndCartographicServiceDataRequest,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.AnyPolygon, ToolType.LandParcel],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      {
        ...this.initialToolsState,
        [ToolType.AnyPolygon]: {
          ...this.initialToolsState[ToolType.AnyPolygon],
          [SourceType.File]: {
            ...this.initialToolsState[ToolType.AnyPolygon][SourceType.File],
            fieldConfigs: getFileLoaderFormFieldConfigs(true),
          },
        },
        [ToolType.LandParcel]: auxiliaryLandParcelToolState,
      },
      this.mapStateService.getLayersState(
        MapId.GeodeticAndCartographicServiceDataRequest,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty ||
      this.areWorkRangesChosen() ||
      this.attachmentsComponent.areChosenDocuments()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getLawPersonFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.areWorkRangesChosen() &&
      this.attachmentsComponent.areDocumentsValid()
    );
  }

  areWorkRangesChosen(): boolean {
    return !!this.mapState.mapObjectTablesState[0].mapObjects?.some(
      (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
    );
  }

  sendRequest(): void {
    this.requestReplyFormComponent
      .askForPostOfficeWhenPostalCodeIsNotFromDictionary()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap(() => this.getRequestBody()),
        switchMap((requestBody) =>
          this.docSignService.addToSign(
            requestBody,
            '/api/interesant/wniosek/udostMatSluzbieGeodezyjnej/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getWorkspaceStateToSave = (): GACSDRNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    workRanges: this.getWorkRanges(),
  });

  handleLoadWorkspaceResponse(state: GACSDRNewRequestFormWorkspaceState): void {
    this.formGroup.patchValue(state.formValue);
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    const mapObjects = state.workRanges.map((workRange) =>
      ShapeUtils.getAnyGeometryMapObject(
        workRange,
        MapObjectApiType.ExtentOrPolygon,
      ),
    );
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.AddNew, mapObjects),
    );
    this.handleMapAction(
      new MapAction(
        MapViewActionType.ExtentToFitToChange,
        MapExtentUtils.getMapExtentFromMapObjects(mapObjects),
      ),
    );
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.SelectRange, mapObjects),
    );
  }

  getWorkRanges(): string[] {
    return [...(this.mapState.mapObjectTablesState[0].mapObjects || [])]
      .filter(
        (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
      )
      .map((workRange) => workRange.geom);
  }

  getFormValue(): GACSDRNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.GeodeticAndCartographicServiceDataRequest}/requests-list`,
    );
  }

  getRequestBody = (): Observable<GACSDRNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        GACSDRNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getWorkRanges(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
